import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import {
  RECEIVABLE_PAYMENT_METHODS,
  type ReceivablePaymentMethodOption,
} from '../constants/receivablePaymentMethods';
import {
  ClinicCustomPaymentMethod,
  customMethodsToSelectOptions,
  parseCustomReceivablePaymentMethods,
  setCustomReceivablePaymentMethodLabels,
} from '../constants/customReceivablePaymentMethods';

export type { ReceivablePaymentMethodOption };

function mergeOptions(customs: ClinicCustomPaymentMethod[]): ReceivablePaymentMethodOption[] {
  const base = RECEIVABLE_PAYMENT_METHODS.map((m) => ({ ...m }));
  const custom = customMethodsToSelectOptions(customs);
  const used = new Set(base.map((b) => b.value.toLowerCase()));
  for (const c of custom) {
    if (used.has(c.value.toLowerCase())) continue;
    base.push(c);
    used.add(c.value.toLowerCase());
  }
  return base;
}

/**
 * Formas de recebimento padrão + métodos customizados da unidade (Configurações da clínica).
 */
export function useReceivablePaymentMethods(enabled = true) {
  const [customMethods, setCustomMethods] = useState<ClinicCustomPaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(getApiUrl('/clinic/settings'))
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data;
        const parsed = parseCustomReceivablePaymentMethods(
          d?.custom_receivable_payment_methods ?? d?.metadata?.custom_receivable_payment_methods,
        );
        setCustomMethods(parsed);
        setCustomReceivablePaymentMethodLabels(parsed);
      })
      .catch(() => {
        if (!cancelled) {
          setCustomMethods([]);
          setCustomReceivablePaymentMethodLabels([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enabled]);

  useEffect(() => {
    const cancel = load();
    return () => { cancel?.(); };
  }, [load]);

  useEffect(() => {
    if (!enabled) return;
    const onSaved = () => { load(); };
    window.addEventListener('clinic-settings:saved', onSaved);
    return () => window.removeEventListener('clinic-settings:saved', onSaved);
  }, [enabled, load]);

  const options = useMemo(() => mergeOptions(customMethods), [customMethods]);

  return { options, customMethods, loading };
}
